import * as moment from 'moment';
import * as Mutations from './mutations';
import * as ROLES from '@/constants/user-types';
import router from '@/router';
import {ActivityDetailsService} from '@/common/services/pages/activity-details.service';
import {PaginationParams} from '@/common/utils/pagination-params';
import { PayNowService } from '../../../common/services/pay-now.service';
import { SchoolAftercareService } from '@/common/services/pages/aftercare.service';
import { SchoolClassService } from '../../../common/services/pages/classes.service';
import { SchoolCampService } from '../../../common/services/pages/camps.service';

export const ActivityChildrenModule = {
    state: {
        class: {
            rosters: {
                rosterInfo: null,
                rosterChild: null,
                rosterRequiredItem: null,
            },
            waitlist: {
                rosterInfo: null,
                rosterChild: null,
                rosterRequiredItem: null,
            }
        },
        camp: {
            rosters: {
                rosterInfo: null,
                rosterChild: null,
                rosterRequiredItem: null,
            },
            waitlist: {
                rosterInfo: null,
                rosterChild: null,
                rosterRequiredItem: null,
            }
        },
        aftercare: {
            rosters: {
                rosterInfo: null,
                rosterChild: null,
                rosterRequiredItem: null,
            },
            waitlist: {
                rosterInfo: null,
                rosterChild: null,
                rosterRequiredItem: null,
            },
            dropin: {
                rosterInfo: null,
                rosterChild: null,
                rosterRequiredItem: null,
                activeDates: null
            }
        },
        activityAddOns: null,
        showDropIn: true,
        isWaitlistAvailable: false,
        // aftercareAvailableWaitlistDays: null,
    },

    mutations: {
        [Mutations.SET_ACTIVITY_CHILDREN_LIST](state, { activityType, listType, list }) {
            state[activityType.toLowerCase()][listType.toLowerCase()] = {
                ...state[activityType.toLowerCase()][listType.toLowerCase()],
                list
            };
        },

        [Mutations.SET_ACTIVITY_ROSTER_INFO](state, { activityType, listType, rosterInfo }) {
            state[activityType.toLowerCase()][listType.toLowerCase()].rosterInfo = rosterInfo;
        },

        [Mutations.SET_ACTIVITY_ROSTER_CHILD](state, { activityType, listType, rosterChild }) {
            state[activityType.toLowerCase()][listType.toLowerCase()].rosterChild = rosterChild;
        },

        [Mutations.SET_ACTIVITY_ROSTER_REQUIRED_ITEM](state, { activityType, listType, rosterRequiredItem }) {
            state[activityType.toLowerCase()][listType.toLowerCase()].rosterRequiredItem = rosterRequiredItem;
        },

        [Mutations.SET_ACTIVITIES_DROP_IN_DATES](state, { activityType, listType, activeDates }) {
            state[activityType.toLowerCase()][listType.toLowerCase()].activeDates = activeDates;
        },

        [Mutations.SET_ACTIVITY_ADDONS](state, activityAddOns) {
            state.activityAddOns = activityAddOns;
        },

        [Mutations.TOGGLE_ACTIVITY_DROP_IN_PAGE](state, showDropIn) {
            state.showDropIn = showDropIn;
        },
        [Mutations.SET_WAITLIST_AVAILABLE](state, isWaitlistAvailable) {
            state.isWaitlistAvailable = isWaitlistAvailable;
        },
        // [Mutations.SET_AFTER_CARE_AVAILABLE_WAITLIST_DAYS](state, aftercareAvailableWaitlistDays) {
        //     state.aftercareAvailableWaitlistDays = aftercareAvailableWaitlistDays;
        // }
    },

    actions: {
        async fetchChildrenByActivity(context, { activityId, activityType, listType }) {
            const { userRole, schoolId } = context.getters;
            const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await ActivityDetailsService.getChildrenByActivity({
                    activityType, listType, schoolId, activityId,
                    params: PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters
                        }
                    })
                });
            context.commit(Mutations.SET_ACTIVITY_CHILDREN_LIST, { activityType, listType, list: content });
            context.dispatch('configPagination', {
                ...pageable,
                numberOfElements,
                totalElements,
                totalPages,
                callback: () => {
                    return context.dispatch('fetchChildrenByActivity', { activityId, activityType, listType });
                }
            });

            (userRole && userRole !== ROLES.SCHOOL_INSTRUCTOR) && context.dispatch(`get${activityType}DetailsGeneralInfo`, activityId);
        },

        async fetchChildInfoForRosterEdit(context, { activityType, listType, rosterInfo }) {
            const { activityId, rosterId, aftercareDayId } = rosterInfo;
            const { data } = await ActivityDetailsService[`getChildInfoFor${activityType}${listType}Edit`](context.getters.schoolId, activityId, rosterId, aftercareDayId);
            context.commit(Mutations.SET_ACTIVITY_ROSTER_CHILD, { activityType, listType, rosterChild: data });
        },

        async removeChild(context, { activityId, rosterId, activityType, listType }) {
            await ActivityDetailsService[`removeChildFrom${activityType}${listType}`](context.getters.schoolId, activityId, rosterId, activityType === 'Aftercare' && context.getters.aftercareDayId);

            await context.dispatch('fetchChildrenByActivity', { activityType, listType, activityId: activityType === 'Aftercare' ? context.getters.aftercareDayId : activityId });
            context.dispatch('createSuccessNotification', 'You have successfully removed a child');
        },

        async removeChildren(context, { activityType, listType, activityId }) {
            const rosterIds = context.getters.selected;
            await ActivityDetailsService[`removeChildrenFrom${activityType}${listType}`](context.getters.schoolId, activityId, rosterIds, activityType === 'Aftercare' && context.getters.aftercareId);

            await context.dispatch('fetchChildrenByActivity', { activityType, listType, activityId });
            context.dispatch('createSuccessNotification', 'You have successfully removed children');
            context.dispatch('resetSelectedItems');
        },

        async removeChildWithoutPayment(context, { activityId, activityType, listType, childId }) {
            await ActivityDetailsService[`removeChildWithoutPaymentFrom${activityType}${listType}`](context.getters.schoolId, activityId, childId);
        },

        prepareRoster(context, { listType, activityType, rosterInfo }) {
            context.dispatch('openModal', {
                modalName: 'RosterBulkEditModal',
                callback: () => context.dispatch('unstageRoster', { activityType, listType }),
                options: { listType, activityType }
            });

            if (rosterInfo) {
                context.commit(Mutations.SET_ACTIVITY_ROSTER_INFO, { listType, activityType, rosterInfo });
                context.dispatch('fetchChildInfoForRosterEdit', { listType, activityType, rosterInfo });
            }
        },

        unstageRoster(context, { listType, activityType }) {
            context.commit(Mutations.SET_ACTIVITY_ROSTER_INFO, { listType, activityType, rosterInfo: null });
            context.commit(Mutations.SET_ACTIVITY_ROSTER_CHILD, { listType, activityType, rosterChild: null });
        },

        async fetchRosterRequired(context, { activityId, listType, activityType }) {
            const { data } = await ActivityDetailsService[`get${activityType}RosterRequired`](context.getters.schoolId, activityId);
            context.commit(Mutations.SET_ACTIVITY_ROSTER_REQUIRED_ITEM, {
                listType,
                activityType,
                rosterRequiredItem: data
            });
        },

        async editChildInList(context, { activityType, listType, form }) {
            const { activityId, rosterId } = context.getters.getRosterInfo(activityType, listType);
            const id = listType === 'Dropin' ? router.app._route.params.id : activityId;
            try {
                context.dispatch('showSpinner');
                await ActivityDetailsService[`edit${activityType}${listType}`](context.getters.schoolId, id, rosterId, form);

                context.dispatch('fetchChildrenByActivity', {
                    activityType,
                    listType,
                    activityId: router.app._route.params.id
                });
            } finally {
                context.dispatch('hideSpinner');
            }
        },

        async editChildrenInList(context, { listType, activityType, activityId, form }) {
            const formWithIDs = {
                ...form,
                ids: context.getters.selected
            };

            try {
                context.dispatch('showSpinner');
                await ActivityDetailsService[`editMultiple${activityType}${listType}`](context.getters.schoolId, activityId, formWithIDs);

                context.dispatch('resetSelectedItems');
                context.dispatch('fetchChildrenByActivity', {
                    activityType,
                    listType,
                    activityId: router.app._route.params.id
                });
            } finally {
                context.dispatch('hideSpinner');
            }
        },

        async fetchAddonsForRosterEdit(context, { activityId, activityType }) {
            await context.dispatch(`get${activityType}AddOns`, activityId);
            let addOns;
            switch (activityType) {
                case 'Class' :
                    addOns = context.getters.classAddOns;
                    break;
                case 'Camp' :
                    addOns = context.getters.campAddOns;
                    break;
                case 'Aftercare' :
                    addOns = context.getters.aftercareAddOns;
                    break;
            }

            context.commit(Mutations.SET_ACTIVITY_ADDONS, addOns)
        },

        clearAddonsForRosterEdit(context) {
            context.commit(Mutations.SET_ACTIVITY_ADDONS, null);
        },

        async addChildToRoster(context, { activityId, activityType, listType, form }) {
            const { childId, parentId } = form;
            delete form.parentId;
            
            let activityIdForForm;
            let activityTypeForRequest;
            switch (activityType) {
                case 'Class' :
                    activityIdForForm = {
                        classId: Number(activityId),
                        childId: Number(childId),
                        schoolId: context.getters.schoolId,
                     };
                     activityTypeForRequest = 'Class'
                    break;
                case 'Camp' :
                    activityIdForForm = { campPricingId: Number(activityId) };
                    activityTypeForRequest = 'Camp'
                    break;
                case 'Aftercare' :
                    activityIdForForm = { aftercareId: Number(activityId) };
                    activityTypeForRequest = listType === 'Dropin' ? 'DropIn' : 'Aftercare';
                    break;
            }

            const fullForm = {
                ...form,
                ...activityIdForForm
            };

            const isPaynowFeature = context.getters.isNewPaymentFeatureEnabled;
            let res = null;

            if (isPaynowFeature && activityType === 'Class') {
                const schoolSlug = context.getters.schoolInfo.portalName;
                res = await PayNowService.enrollChild(schoolSlug, activityId, fullForm);            
            } else {
                const response = await ActivityDetailsService[`addChildTo${activityType}${listType}`](context.getters.schoolId, activityId, fullForm);
                res = response.data;
            }
        
            if (context.getters.userInfo.userRole === 'SCHOOL_ADMIN' || context.getters.userInfo.userRole === 'SCHOOL_DIRECTOR') {
                // new payment flow
                if (isPaynowFeature) {
                    context.dispatch('showSpinner');
                    try {
                        context.dispatch('setPayNowEnrollemtData', {
                            activityId,
                            childId,
                            activityType: activityTypeForRequest,
                        })
                        // charge flow
                        if (context.getters.isChargeFlow && res && res.id) {
                            context.dispatch('setPayNowActivityId', res.id);
                            const methods = await PayNowService.getAllPaymentMethods(
                                parentId,
                                context.getters.schoolId
                              );
                              const defaultPaymentMethod = methods.find(method => method.default);
                              if (defaultPaymentMethod) {
                                context.dispatch('createPayment', defaultPaymentMethod.id);
                                context.dispatch('setChargeFlow', false);
                              } else {
                                context.dispatch('createWarningNotification', 'Parent does not have any selected payment method');
                              }
          
                            // invoice flow
                        } else if(!context.getters.isChargeFlow && res && res.id) {
                            context.dispatch('setPayNowActivityId', res.id);
                            context.dispatch('createInvoicePayment', res.id);
                        }
                    } catch (error) {
                        context.dispatch('createErrorNotification', 'Error during payment process');
                    } finally {
                        context.dispatch('hideSpinner');
                    }
                } else {
                    // old payment flow
                    context.dispatch('getInvoicePaymentInfo', { activityId, activityType, listType, childId, couponCode: null, form });
                    context.dispatch('openModal', {
                        modalName: 'InvoicePaymentModal',
                        options: { activityId },
                        callback: () => {
                            if(context.getters.submitClick) {
                                activityType === 'Aftercare'
                                    ? setTimeout(context.dispatch('getAftercareList'), 3000)
                                    : setTimeout(context.dispatch('fetchChildrenByActivity', { activityId, activityType, listType }), 3000)
                            } else {
                                setTimeout(context.dispatch('removeChildWithoutPayment',  { activityId, activityType, listType, childId }), 3000)
                            }
                        }
                    });
                }
            } else {
                activityType === 'Aftercare'
                    ? setTimeout(context.dispatch('getAftercareList'), 3000)
                    : setTimeout(context.dispatch('fetchChildrenByActivity', { activityId, activityType, listType }), 3000)
            }
        },

        async isWaitlistAvailable(context, { activityId, activityType }) {
            let activityData;
            let activityStatistics;

            switch (activityType) {
                case 'Class' :
                    const { data: classData } = await SchoolClassService.getClass(context.getters.schoolId, activityId);
                    activityData = classData;
                    const { data: classStatistics } = await SchoolClassService.getClassDetailsGeneralInfo(context.getters.schoolId, activityId);
                    activityStatistics = classStatistics;
                    context.commit(Mutations.SET_WAITLIST_AVAILABLE, activityData.waitList && activityData.maxEnroll <= activityStatistics.enrolled && activityData.maxWaitList > activityStatistics.waitList);
                    break;
                case 'Camp' :
                    const { data } = await SchoolCampService.getCampDetailsGeneralInfo(context.getters.schoolId, activityId);
                    // if some of the dat keys is null map these keys to 0
                    const campData = Object.keys(data).reduce((acc, key) => {
                        acc[key] = data[key] || 0;
                        return acc;
                    }, {});
                    context.commit(Mutations.SET_WAITLIST_AVAILABLE, campData.enrolled >= campData.maxEnroll && campData.waitList < campData.maxWaitList);
                    break;
                case 'Aftercare' :
                    // const { data: aftercareData } = await SchoolAftercareService.getAftercareSession(context.getters.schoolId, activityId);
                    // const response = await Promise.all(aftercareData.days.map(async day => {
                    // const dayStatistics = await SchoolAftercareService.getAftercareDetailsGeneralInfo(context.getters.schoolId, day.id);
                    //     return {
                    //         ...day,
                    //         ...dayStatistics.data,
                    //     };
                    // }));
                    // const aftercareAvailableWaitlistDays = response.filter(day => day.maxEnroll === day.enrolled && day.maxWaitList > day.waitList);
                    // context.commit(Mutations.SET_AFTER_CARE_AVAILABLE_WAITLIST_DAYS, aftercareAvailableWaitlistDays);
                    // context.commit(Mutations.SET_WAITLIST_AVAILABLE, aftercareAvailableWaitlistDays.length > 0);
                    break;
            }
        },

        async openAddChildToRosterModal(context, payload) {
            const activityId = router.app._route.params.id ? router.app._route.params.id : payload.id;
            const { activityType, listType } = payload;

            const isAftercareWaitlist = activityType === 'Aftercare' && listType === 'Waitlist';

            await Promise.all([
                activityType !== 'Aftercare' && context.dispatch('fetchAddonsForRosterEdit', { activityId, activityType }),
                !isAftercareWaitlist && context.dispatch('fetchRosterRequired', { activityId, activityType, listType }),
                context.dispatch('getChildrenForEnrollment'),
                context.dispatch('getAllPickupOptions'),
                context.dispatch('getAllTeachers'),
                context.dispatch('getTShirts'),
                !isAftercareWaitlist && context.dispatch('isWaitlistAvailable', { activityId, activityType }),
            ]);

            await context.dispatch('openModal', {
                modalName: 'AddChildToRoster',
                options: { ...payload, activityId }
            })
        },

        toggleActivityDropInPage(context, toggle) {
            context.commit(Mutations.TOGGLE_ACTIVITY_DROP_IN_PAGE, toggle);
        },

        async getDropInDates(context, { activityId, activityType, listType, childId }) {
            const { data } = await ActivityDetailsService.getDropInDates({
                schoolId: context.getters.schoolId,
                activityId,
                childId,
                activityType
            });
            context.commit(Mutations.SET_ACTIVITIES_DROP_IN_DATES, {
                activityType,
                listType,
                activeDates: data
            });
        },

        async addChildrenToWaitlist(context,  { activityId, activityType, form }) {
            try {
        
                switch (activityType) {
                    case 'Class' :

                        await ActivityDetailsService[`addChildTo${activityType}Waitlist`](context.getters.schoolId, activityId, {
                            classId: Number(activityId),
                            childId: form.childId
                        });
                        context.dispatch('fetchChildrenByActivity', { activityType, listType: 'Waitlist', activityId });
                        break;
                    case 'Camp' :
                        await SchoolCampService.addToWaitList(context.getters.schoolId, {
                            campPricingId: Number(activityId),
                            ...form
                        });
                        context.dispatch('fetchChildrenByActivity', { activityType, listType: 'Waitlist', activityId });

                        break;
                    case 'Aftercare' :
                        const { data } = await SchoolAftercareService.getAftercareSession(context.getters.schoolId, activityId);
                        
                        const aftercareDayIds = data.days.filter((day) => form[day.day.toLowerCase()]).map((day) => day.id);

                        const payload = {
                            aftercareId: Number(activityId),
                            childId: form.childId,
                            aftercareDayIds,
                            addOnIds: form.addOnIds,
                            pickUpOptionId: form.pickUpOptionId,
                            teacherId: form.teacherId,
                            tshirtSizeId: form.tshirtSizeId,
                        };
                        
                        await ActivityDetailsService[`addChildTo${activityType}Waitlist`](context.getters.schoolId, activityId, payload);
                        context.dispatch('getAftercareList')
                        break;
                }


         

      
                context.dispatch('createSuccessNotification', 'You have successfully added children to waitlist');
             
            } catch (error) {
                context.dispatch('createErrorNotification', 'Error during adding children to waitlist');
            }
        }
    },

    getters: {
        getChildrenByActivity: (state, getters, rootState, rootGetters) => (activityType = 'Class', listType = 'Rosters') => {
            const key = state[_.lowerCase(activityType)][_.lowerCase(listType)];
            return key && key.list && key.list.map(child => ({
                ...child,
                activityId: child.classId || child.campPricingId || child.aftercareId,
                birthDate: child.birthDate && moment(child.birthDate).format(rootGetters.dateFormat.toUpperCase()),
                allergies: child.allergies || 'none'
            }))
        },

        getRosterChild: state => (activityType = 'Class', listType = 'Rosters') => {
            const key = state[_.lowerCase(activityType)][_.lowerCase(listType)];
            return key && key.rosterChild;
        },

        getRosterInfo: state => (activityType = 'Class', listType = 'Rosters') => {
            const key = state[_.lowerCase(activityType)][_.lowerCase(listType)];
            return key && key.rosterInfo;
        },

        getRosterRequired: state => (activityType = 'Class', listType = 'Rosters') => {
            const key = state[_.lowerCase(activityType)][_.lowerCase(listType)];
            return key && key.rosterRequiredItem;
        },

        datesDropIn: state => (activityType = 'Aftercare', listType = 'Dropin') => {
            const key = state[_.lowerCase(activityType)][_.lowerCase(listType)];
            return key && key.activeDates;
        },

        activityAddOns: state => state.activityAddOns,

        showDropIn: state => state.showDropIn,

        isWaitlistAvailable: state => state.isWaitlistAvailable,

        // aftercareAvailableWaitlistDays: state => state.aftercareAvailableWaitlistDays
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/activity-children-list/activity-children.module.js